import 'react-image-crop/dist/ReactCrop.css';
import type { ReactNode } from 'react';
import type { Crop, PixelCrop } from 'react-image-crop';
import { useRef, useState } from 'react';
import ReactCrop from 'react-image-crop';
import css from './image-crop-modal.module.scss';

export type ImageCropModalProps = {
  src: string;
  open: boolean;
  // Suggested name for the resulting cropped File. Defaults to
  // `cropped-{timestamp}.png`.
  fileName?: string;
  // Optional aspect ratio (width/height). Omit for free-form crops.
  aspect?: number;
  onClose: () => void;
  // Receives the cropped image as a File. The consumer is expected to
  // pipe this into their `startUpload([file])` for re-upload.
  onCropped: (file: File) => void;
  title?: ReactNode;
};

const cropToBlob = async (
  image: HTMLImageElement,
  crop: PixelCrop
): Promise<Blob | null> => {
  const canvas = document.createElement('canvas');
  const scaleX = image.naturalWidth / image.width;
  const scaleY = image.naturalHeight / image.height;
  canvas.width = crop.width * scaleX;
  canvas.height = crop.height * scaleY;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  ctx.drawImage(
    image,
    crop.x * scaleX,
    crop.y * scaleY,
    crop.width * scaleX,
    crop.height * scaleY,
    0,
    0,
    crop.width * scaleX,
    crop.height * scaleY
  );

  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/png');
  });
};

export const ImageCropModal = ({
  src,
  open,
  fileName,
  aspect,
  onClose,
  onCropped,
  title = 'Crop image',
}: ImageCropModalProps) => {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop | undefined>();
  const [completed, setCompleted] = useState<PixelCrop | null>(null);
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const handleSave = async () => {
    if (!completed || !imageRef.current) return;
    setBusy(true);
    try {
      const blob = await cropToBlob(imageRef.current, completed);
      if (!blob) {
        setBusy(false);
        return;
      }
      const name = fileName ?? `cropped-${Date.now()}.png`;
      onCropped(new File([blob], name, { type: 'image/png' }));
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      aria-modal="true"
      className={css.backdrop}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      onKeyDown={(event) => {
        if (event.key === 'Escape') onClose();
      }}
      role="dialog"
      tabIndex={-1}
    >
      <div className={css.modal}>
        <div className={css.header}>
          <span className={css.title}>{title}</span>
          <button
            aria-label="Close"
            className={css.close}
            onClick={onClose}
            type="button"
          >
            ×
          </button>
        </div>
        <div className={css.canvas}>
          <ReactCrop
            aspect={aspect}
            crop={crop}
            onChange={(next) => setCrop(next)}
            onComplete={(next) => setCompleted(next)}
          >
            {/* crossOrigin lets the canvas export the crop — utfs.io serves
                CORS headers, so without this the canvas taints and toBlob
                throws. */}
            <img alt="" crossOrigin="anonymous" ref={imageRef} src={src} />
          </ReactCrop>
        </div>
        <div className={css.footer}>
          <button
            className={css.btn}
            disabled={busy}
            onClick={onClose}
            type="button"
          >
            Cancel
          </button>
          <button
            className={`${css.btn} ${css.btnPrimary}`}
            disabled={!completed || busy}
            onClick={handleSave}
            type="button"
          >
            {busy ? 'Saving…' : 'Save crop'}
          </button>
        </div>
      </div>
    </div>
  );
};
